import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertTriangle,
  Scale,
  Shield,
  FileText,
  CheckCircle
} from 'lucide-react';

const LegalDisclaimerModal = ({ onAccept }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    // Show disclaimer when user signs in (every time)
    if (currentUser) {
      const hasAcceptedThisSession = sessionStorage.getItem(
        'briefed-legal-disclaimers-accepted-session'
      );
      if (!hasAcceptedThisSession) {
        setIsVisible(true);
      }
    }
  }, [currentUser]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isVisible) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalTop = window.getComputedStyle(document.body).top;
      const scrollY = window.scrollY;

      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      const preventTouchMove = (e) => {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', preventTouchMove, {
        passive: false
      });
      document.addEventListener('touchstart', preventTouchMove, {
        passive: false
      });

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('touchstart', preventTouchMove);
        window.scrollTo(0, scrollY);
      };
    }
  }, [isVisible]);

  const handleAccept = () => {
    // Store acceptance for this session only
    sessionStorage.setItem(
      'briefed-legal-disclaimers-accepted-session',
      'true'
    );
    setIsVisible(false);
    if (onAccept) onAccept();
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleAccept();
        }
      }}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          handleAccept();
        }
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-content"
        data-modal-content
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => {
          e.stopPropagation();
          const modalContent = e.currentTarget;
          const isScrollingModal =
            modalContent.scrollHeight > modalContent.clientHeight;
          if (!isScrollingModal) {
            e.preventDefault();
          }
        }}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Important Legal Disclaimers
            </h2>
          </div>

          {/* Content */}
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <Scale className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Not Legal Advice
                </h3>
                <p className="text-sm text-gray-700">
                  Briefed provides data and research tools only. This service
                  does not constitute legal advice. Consult qualified legal
                  professionals for legal advice and representation.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Data Accuracy
                </h3>
                <p className="text-sm text-gray-700">
                  While we strive for accuracy, we cannot guarantee the
                  completeness or accuracy of all court data. Verify important
                  information independently before making legal decisions.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-gray-900 mb-1">
                  Professional Use
                </h3>
                <p className="text-sm text-gray-700">
                  This service is intended for legal professionals and
                  authorized users. Use of this service constitutes acceptance
                  of our{' '}
                  <a
                    href="/terms"
                    className="text-blue-600 hover:text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    className="text-blue-600 hover:text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleAccept}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />I Understand and Accept
            </button>
            <button
              onClick={() => (window.location.href = '/')}
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

LegalDisclaimerModal.propTypes = {
  onAccept: PropTypes.func
};

export default LegalDisclaimerModal;
